import { DatabaseConnectionGroup } from '@6grain/masofa-monolith-api-client/common/model/databaseConnectionGroup';
import { DatabaseConnectionsSnapshot } from '@6grain/masofa-monolith-api-client/common/model/databaseConnectionsSnapshot';
import { DatabaseSizeRow } from '@6grain/masofa-monolith-api-client/common/model/databaseSizeRow';
import { DatabaseTableSizeRow } from '@6grain/masofa-monolith-api-client/common/model/databaseTableSizeRow';

export const DATABASE_CONNECTIONS_POLL_MS = 15_000;
export const DATABASE_CONNECTION_GROUPS_PAGE_SIZE = 100;

const KIB = 1024;
const MIB = KIB * KIB;
const GIB = MIB * KIB;

export function readDatabaseSizeRows(
  snapshot: DatabaseConnectionsSnapshot | null | undefined,
): DatabaseSizeRow[] {
  return snapshot?.databases ?? [];
}

export function readTotalDatabaseSizeBytes(
  snapshot: DatabaseConnectionsSnapshot | null | undefined,
): number {
  if (typeof snapshot?.totalDatabaseSizeBytes === 'number' && Number.isFinite(snapshot.totalDatabaseSizeBytes)) {
    return snapshot.totalDatabaseSizeBytes;
  }
  return readDatabaseSizeRows(snapshot).reduce((sum, row) => sum + (row.sizeBytes ?? 0), 0);
}

export function readDatabaseTables(row: DatabaseSizeRow | null | undefined): DatabaseTableSizeRow[] {
  return row?.tables ?? [];
}

type DiskVolumeSnapshot = {
  diskTotalBytes?: number | null;
  diskAvailableBytes?: number | null;
};

export function readDiskVolume(
  snapshot: DiskVolumeSnapshot | null | undefined,
): { totalBytes: number | null; availableBytes: number | null } {
  return {
    totalBytes: asFiniteNumber(snapshot?.diskTotalBytes),
    availableBytes: asFiniteNumber(snapshot?.diskAvailableBytes),
  };
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formatDatabaseSizeBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes >= GIB) {
    return `${(bytes / GIB).toFixed(2)} GiB`;
  }
  if (bytes >= MIB) {
    return `${(bytes / MIB).toFixed(2)} MiB`;
  }
  if (bytes >= KIB) {
    return `${(bytes / KIB).toFixed(2)} KiB`;
  }
  return `${bytes} B`;
}

const HISTORY_INFIX = /-history-/i;
const HISTORY_SUFFIX = /-history$/i;
const ENV_SUFFIX = /-(prod|dev|local|test|docker|stage|staging)$/i;

export interface DatabaseSizePairMember {
  name: string;
  hint: 'Live' | 'History';
  sizeBytes: number;
  tables: DatabaseTableSizeRow[];
  row: DatabaseSizeRow;
}

export interface DatabaseSizePair {
  id: string;
  label: string;
  sizeBytes: number;
  members: DatabaseSizePairMember[];
}

export function liveDatabaseNameFor(name: string | null | undefined): string | null {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) {
    return null;
  }
  if (HISTORY_INFIX.test(trimmed)) {
    return trimmed.replace(HISTORY_INFIX, '-');
  }
  if (HISTORY_SUFFIX.test(trimmed)) {
    return trimmed.replace(HISTORY_SUFFIX, '');
  }
  return null;
}

export function historyDatabaseNameFor(liveName: string): string {
  const trimmed = liveName.trim();
  if (ENV_SUFFIX.test(trimmed)) {
    return trimmed.replace(ENV_SUFFIX, '-history-$1');
  }
  return `${trimmed}-history`;
}

export function groupDatabaseSizeRows(rows: DatabaseSizeRow[] | null | undefined): DatabaseSizePair[] {
  const list = rows ?? [];
  const byName = new Map<string, DatabaseSizeRow>();
  for (const row of list) {
    const name = row.name?.trim() ?? '';
    if (name) {
      byName.set(name.toLowerCase(), row);
    }
  }

  const used = new Set<string>();
  const pairs: DatabaseSizePair[] = [];

  for (const row of list) {
    const name = row.name?.trim() ?? '';
    if (!name) {
      continue;
    }
    const key = name.toLowerCase();
    if (used.has(key)) {
      continue;
    }

    const liveName = liveDatabaseNameFor(name);
    if (liveName) {
      const live = byName.get(liveName.toLowerCase());
      if (live?.name?.trim()) {
        continue;
      }
      used.add(key);
      pairs.push(toPair(null, row));
      continue;
    }

    const history = byName.get(historyDatabaseNameFor(name).toLowerCase());
    used.add(key);
    if (history?.name?.trim()) {
      used.add(history.name.trim().toLowerCase());
      pairs.push(toPair(row, history));
    } else {
      pairs.push(toPair(row, null));
    }
  }

  return pairs.sort((left, right) => {
    const sizeDelta = right.sizeBytes - left.sizeBytes;
    return sizeDelta !== 0 ? sizeDelta : left.label.localeCompare(right.label);
  });
}

export function flattenDatabaseSizePairs(pairs: DatabaseSizePair[]): DatabaseSizeRow[] {
  return pairs.flatMap(pair => pair.members.map(member => member.row));
}

export function databaseSizeSearchFields(row: DatabaseSizeRow): Array<string | null | undefined> {
  return [
    row.name,
    formatDatabaseSizeBytes(row.sizeBytes),
    ...readDatabaseTables(row).flatMap(table => [
      table.schema,
      table.name,
      table.schema && table.name ? `${table.schema}.${table.name}` : undefined,
    ]),
  ];
}

export function databaseSizePairSearchFields(pair: DatabaseSizePair): Array<string | null | undefined> {
  return pair.members.flatMap(member => databaseSizeSearchFields(member.row));
}

function toPair(live: DatabaseSizeRow | null, history: DatabaseSizeRow | null): DatabaseSizePair {
  const members: DatabaseSizePairMember[] = [];
  if (live?.name?.trim()) {
    members.push(toMember(live, 'Live'));
  }
  if (history?.name?.trim()) {
    members.push(toMember(history, 'History'));
  }
  const label = members[0]?.name ?? '';
  return {
    id: label,
    label,
    sizeBytes: members.reduce((sum, member) => sum + member.sizeBytes, 0),
    members,
  };
}

function toMember(row: DatabaseSizeRow, hint: 'Live' | 'History'): DatabaseSizePairMember {
  return {
    name: row.name?.trim() ?? '',
    hint,
    sizeBytes: row.sizeBytes ?? 0,
    tables: readDatabaseTables(row),
    row,
  };
}

export function databaseConnectionGroupSearchFields(
  group: DatabaseConnectionGroup,
): Array<string | null | undefined> {
  return [
    group.databaseName,
    group.userName,
    group.applicationName,
    group.clientAddress,
    group.state,
    group.waitEventType,
    group.waitEvent,
    group.connectionCount == null ? undefined : String(group.connectionCount),
  ];
}

export function slotPressureTagColor(pressure: string | null | undefined): 'success' | 'info' | 'danger' {
  if (pressure === 'Exhausted') {
    return 'danger';
  }
  if (pressure === 'Low') {
    return 'info';
  }
  return 'success';
}

export function slotPressureLabelKey(pressure: string | null | undefined): string {
  if (pressure === 'Exhausted') {
    return 'Slot pressure Exhausted';
  }
  if (pressure === 'Low') {
    return 'Slot pressure Low';
  }
  return 'Slot pressure Ok';
}

export function visibilityLabelKey(visibility: string | null | undefined): string {
  return visibility === 'OwnSessions' ? 'Own sessions only' : 'All sessions';
}
